import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { TFunction } from 'next-i18next';
import getConfig from 'next/config';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const { publicRuntimeConfig } = getConfig();

export const isProduction = (): boolean => publicRuntimeConfig.ENV === 'production';

export const getDateTimeUTC = (date: Date, time: string): string => {
  const t = dayjs(time, 'HH:mma');
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    t.hour(),
    t.minute()
  ).toISOString();
};

export const formatTime = (input: string): string => {
  input = input.toLowerCase();
  let time = dayjs(input, 'HH:mma');
  if (!time.isValid()) {
    time = dayjs(input, 'HHa');
  }
  if (!time.isValid()) {
    time = dayjs(input, 'HH:mm');
  }
  if (input.length <= 2 && !isNaN(parseInt(input)) && !time.isValid()) {
    time = dayjs(input, 'HH');
  }
  if (time.format('h:mma') === 'Invalid Date') {
    return input;
  } else {
    return time.format('h:mma');
  }
};

export const validTime = (time: string): boolean => {
  if (new RegExp('^(0[0-9]|1[0-2]|[0-9]):[0-5][0-9](am|pm)$').test(time)) {
    return true;
  } else {
    return false;
  }
};

export const toMinutesSeconds = (secs: number): string => {
  if (secs < 60) return secs.toString();
  const m = Math.trunc(secs / 60);
  const s = ('0' + (secs % 60)).slice(-2);
  return `${m}:${s}`;
};

export const toMinutesSecondsWithLabel = (secsRaw: number, t?: TFunction): string => {
  const secs = Math.round(secsRaw);
  const label = secs >= 60 ? 'min' : 'sec';
  return t
    ? t(`common:${label}`, { time: toMinutesSeconds(secs) })
    : `${toMinutesSeconds(secs)} ${label}`;
};

export const timeDifference = (firstDate: Date, secondDate: Date): string => {
  let totalSeconds = 0;

  if (firstDate > secondDate) {
    totalSeconds = dayjs(firstDate).diff(secondDate, 'second');
  } else {
    totalSeconds = dayjs(secondDate).diff(firstDate, 'second');
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalSeconds / 60 / 60);

  const seconds = totalSeconds % 60;
  const minutes = totalMinutes % 60;
  const hours = totalHours % 60;

  let displayTime = `${seconds.toString().padStart(2, '0')}`;

  if (hours > 0) {
    displayTime = `${hours}:${minutes.toString().padStart(2, '0')}:${displayTime}`;
  } else {
    displayTime = `${minutes}:${displayTime}`;
  }

  if (firstDate > secondDate) {
    return `-${displayTime}`;
  }

  return displayTime;
};

export const futureDate = (days: number): Date => dayjs().add(days, 'day').toDate();

export const passDate = (days: number): Date =>
  dayjs()
    .subtract(days * 24, 'hour')
    .toDate();

/**
 * Formats a date using dayjs. Takes an optional argument for passing in custom date formats
 * @param date the date string to be formatted
 * @param customFormat This is an optional argument that must be an array consisting of two values:
 * the first being the time format, the second being the date format. See the DayJS docs for valid
 * format syntax.
 * @returns a two-item array with the following shape: [formatted time, formatted date]
 */
export const formatDate = (
  date: string,
  customFormat?: [timeFormat: string, dateFormat: string]
): string[] => {
  const defaultTimeFormat = 'h:mma';
  const defaultDateFormat = 'dddd, MMMM D';
  return [
    dayjs(date).format(!!customFormat && !!customFormat[0] ? customFormat[0] : defaultTimeFormat),
    dayjs(date).format(!!customFormat && !!customFormat[1] ? customFormat[1] : defaultDateFormat),
  ];
};

export const zeroHourISOString = (date: Date, timezone = dayjs.tz.guess()): string => {
  return dayjs
    .tz(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}T00:00:00`, timezone)
    .toISOString();
};

export const finalHourISOString = (date: Date, timezone = dayjs.tz.guess()): string => {
  return dayjs
    .tz(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}T23:59:59`, timezone)
    .toISOString();
};

export const btoa = (input: string): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  const str = input;
  let output = '';

  for (
    let block = 0, charCode, i = 0, map = chars;
    str.charAt(i | 0) || ((map = '='), i % 1);
    output += map.charAt(63 & (block >> (8 - (i % 1) * 8)))
  ) {
    charCode = str.charCodeAt((i += 3 / 4));
    if (charCode > 0xff) {
      throw new Error('bad character.');
    }
    block = (block << 8) | charCode;
  }
  return output;
};

export const recurrenceType = (
  recurrence: string,
  t: TFunction,
  endRepeat?: string | null
): string | null => {
  let label: string | null = null;
  switch (recurrence) {
    case 'ONCE':
      return null;
    case 'DAILY':
      label = t('daily');
      break;
    case 'WEEKLY':
      label = t('weekly');
      break;
    case 'MONTHLY':
      label = t('monthly');
      break;
    case 'YEARLY':
      label = t('yearly');
      break;
    default:
      return null;
  }
  if (endRepeat) {
    return `${label} ${t('until')} ${dayjs(endRepeat).format('MMMM D')}`;
  }
  return label;
};

const offsetRegex = new RegExp(/^[1-9][0-9]?$/);
export const validOffset = (url: string): boolean => offsetRegex.test(url);

export const getNegativeValue = (number: number): number => {
  return number === 0 ? number : -number;
};

export const serviceTimeIsUpcoming = (date: Date): boolean => {
  return dayjs(date).isAfter(dayjs());
};

export const serviceTimeHasPast = (date: Date): boolean => {
  return dayjs().diff(date) >= 0;
};

export const capitalizeFirstCharacter = (s: string): string => {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const formatNumberWithCommas = (num: number): string => Number(num).toLocaleString('en-US');
